import type {
  FilePickerProvider,
  PickFilesOptions,
  PickedFile,
  SaveBytesOptions,
  SaveOutcome,
} from '../types.js'

/**
 * Web file picker/save with deterministic fallbacks — PRD §10.1, §10.7.
 * Prefer scoped pickers; fall back to input/download; never request broad
 * storage permission. All DOM access is behind injectable functions so tests
 * can verify the fallback order without a browser.
 */

interface FileLike {
  readonly name: string
  readonly type: string
  arrayBuffer(): Promise<ArrayBuffer>
}

interface InputElementLike {
  type: string
  accept: string
  multiple: boolean
  files: ArrayLike<FileLike> | null
  onchange: (() => void) | null
  oncancel: (() => void) | null
  click(): void
}

interface AnchorLike {
  href: string
  download: string
  click(): void
}

interface DocumentLike {
  createElement(tag: string): unknown
}

export interface WebFilePickerEnvironment {
  readonly show_open_file_picker?: (options: PickFilesOptions) => Promise<readonly PickedFile[]>
  readonly show_save_file_picker?: (options: SaveBytesOptions) => Promise<SaveOutcome>
  readonly download?: (options: SaveBytesOptions) => void
  readonly has_dom?: boolean
}

function documentLike(): DocumentLike | undefined {
  return (globalThis as { document?: DocumentLike }).document
}

function defaultDownload(options: SaveBytesOptions): void {
  const document = documentLike()
  if (document === undefined) return
  const blob = new Blob([new Uint8Array(options.bytes)], { type: options.media_type })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a') as AnchorLike
  anchor.href = url
  anchor.download = options.suggested_name
  anchor.click()
  URL.revokeObjectURL(url)
}

export function createWebFilePickerProvider(
  env: WebFilePickerEnvironment = {},
): FilePickerProvider {
  const hasDom = env.has_dom ?? documentLike() !== undefined

  async function pickFiles(options: PickFilesOptions): Promise<readonly PickedFile[]> {
    if (env.show_open_file_picker !== undefined) {
      return env.show_open_file_picker(options)
    }
    const document = documentLike()
    if (!hasDom || document === undefined) return []
    return new Promise<readonly PickedFile[]>((resolve) => {
      const input = document.createElement('input') as InputElementLike
      input.type = 'file'
      input.accept = options.accept.join(',')
      input.multiple = options.multiple === true
      input.onchange = () => {
        const files = Array.from(input.files ?? [])
        void (async () => {
          const picked: PickedFile[] = []
          for (const file of files) {
            picked.push({
              name: file.name,
              media_type: file.type,
              bytes: new Uint8Array(await file.arrayBuffer()),
            })
          }
          resolve(picked)
        })()
      }
      input.oncancel = () => resolve([])
      input.click()
    })
  }

  async function saveBytes(options: SaveBytesOptions): Promise<SaveOutcome> {
    if (env.show_save_file_picker !== undefined) {
      const outcome = await env.show_save_file_picker(options)
      if (outcome === 'saved' || outcome === 'cancelled') return outcome
    }
    const download = env.download ?? (hasDom ? defaultDownload : undefined)
    if (download === undefined) return 'unavailable'
    download(options)
    // A download cannot confirm a write; callers treat this as initiated only.
    return 'downloaded'
  }

  return { pickFiles, saveBytes }
}
